import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { ChevronRight, Info, ListTree } from 'lucide-react';
import PaginationBar from '../../PlatformAdmin/PaginationBar';
import RunDetailDrawer from '../RunDetail/RunDetailDrawer';
import { n0, formatDurationMs, formatCost, RUN_TYPE_LABELS } from './usageFormat';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './UsageAnalyticsPanel.module.scss';
import runStyles from '../RunDetail/RunDetail.module.scss';

const PAGE_SIZE = 25;

const formatWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const runLabel = (run) => run.agent_name || run.model_label || run.user_name || `Run #${run.id ?? ''}`;

/**
 * Drill-down leaf (spec §2 `/usage/runs`): individual runs behind a clicked
 * breakdown row (or a "view runs" action), paginated.
 *
 * A run with a trace opens the shared Run Details drawer — the same component
 * the Agent UI uses, pointed at this surface's `/runs/{id}` endpoint. Runs
 * without one (`has_trace: false`) still list: they ran before tracing shipped,
 * or their trace passed the 90-day retention window.
 */
const UsageRunsDialog = ({ open, title, filters, service, onClose, redactedMessage = null }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null); // { runId, hint }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await service.getRuns({ ...filters, limit: PAGE_SIZE, offset });
      setRows(data?.items || data?.runs || []);
      setTotal(data?.total ?? (data?.items || data?.runs || []).length);
    } catch (err) {
      setError(err?.data?.detail || err?.message || 'Failed to load runs.');
    } finally {
      setLoading(false);
    }
  }, [service, filters, offset]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  useEffect(() => {
    setOffset(0);
  }, [filters]);

  // Stable identity — the drawer refetches whenever `fetchDetail` changes.
  // `company_id` is carried through so a platform admin's lookup stays scoped to
  // the company they drilled into.
  const companyId = filters?.company_id;
  const fetchDetail = useCallback(
    (runId) => service.getRunDetail(runId, companyId ? { company_id: companyId } : {}),
    [service, companyId]
  );

  const anyWithoutTrace = useMemo(() => rows.some((r) => r.has_trace === false), [rows]);

  const openRun = (run) => {
    const runId = run?.run_id || run?.id;
    if (!runId || run.has_trace === false) return;
    setSelectedRun({ runId, hint: run });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} classes={{ paper: sharedStyles.dialogPaper }} maxWidth="md" fullWidth>
        <DialogTitle className={sharedStyles.dialogTitle}>Runs — {title}</DialogTitle>
        <DialogContent className={sharedStyles.dialogContent}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {anyWithoutTrace && (
            <Box className={runStyles.notice} sx={{ mb: 1.5 }}>
              <Info size={15} aria-hidden />
              <span>
                Runs marked <strong>No detail</strong> predate run tracing or have passed the 90-day
                retention window. Their metrics are still accurate.
              </span>
            </Box>
          )}

          {loading && rows.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : rows.length === 0 ? (
            <Box className={sharedStyles.emptyState} sx={{ p: 4 }}>
              <ListTree className={sharedStyles.emptyIcon} size={44} />
              <Typography variant="h6">No runs</Typography>
              <Typography variant="body2">Nothing recorded for this selection.</Typography>
            </Box>
          ) : (
            // tableCard sets overflow:hidden (fine for full-page panels that scroll at
            // the page level) — inside this fixed-height Dialog that silently clips
            // extra rows with no scrollbar, so this list needs its own scroll region.
            <Box className={sharedStyles.tableCard} sx={{ maxHeight: '48vh', overflowY: 'auto' }}>
              {rows.map((run, idx) => {
                const cost = formatCost(run.estimated_cost, run.cost_coverage_pct ?? (run.estimated_cost != null ? 100 : null));
                const openable = Boolean(run.run_id || run.id) && run.has_trace !== false;
                return (
                  <Box
                    key={run.run_id || run.id || idx}
                    className={sharedStyles.userRow}
                    sx={{ flexWrap: 'wrap', cursor: openable ? 'pointer' : 'default' }}
                    role={openable ? 'button' : undefined}
                    tabIndex={openable ? 0 : undefined}
                    onClick={() => openRun(run)}
                    onKeyDown={(e) => {
                      if (openable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        openRun(run);
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: '1 1 220px', minWidth: 0 }}>
                      <span className={sharedStyles.companyName}>{runLabel(run)}</span>
                      <span className={sharedStyles.companyMeta}>
                        {run.local_date || formatWhen(run.started_at)} · {formatWhen(run.started_at)}
                      </span>
                    </Box>
                    <span className={sharedStyles.actionTag}>{RUN_TYPE_LABELS[run.run_type] || run.run_type || '—'}</span>
                    <span
                      className={`${sharedStyles.statusPill} ${run.status === 'error' ? sharedStyles.inactive : sharedStyles.active}`}
                    >
                      {run.status || '—'}
                    </span>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 90 }}>
                      <span className={styles.runMetric}>{n0(run.total_tokens)} tok</span>
                      <span className={sharedStyles.muted}>{formatDurationMs(run.duration_ms ?? run.avg_duration_ms)}</span>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 110 }}>
                      <span className={styles.runMetric}>{cost.text}</span>
                      {run.cost_source && <span className={sharedStyles.muted}>via {run.cost_source}</span>}
                    </Box>
                    {openable ? (
                      <ChevronRight size={16} aria-hidden style={{ flexShrink: 0 }} />
                    ) : (
                      <span className={runStyles.noTraceTag}>No detail</span>
                    )}
                    {run.error_message && (
                      <Box sx={{ flexBasis: '100%' }}>
                        <Typography variant="caption" color="error">
                          {run.error_message}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {total > 0 && (
            <PaginationBar total={total} limit={PAGE_SIZE} offset={offset} loading={loading} unit="runs" onOffsetChange={setOffset} />
          )}
        </DialogContent>
        <DialogActions className={sharedStyles.dialogActions}>
          <Button onClick={onClose} className={sharedStyles.cancelButton}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <RunDetailDrawer
        open={Boolean(selectedRun)}
        runId={selectedRun?.runId}
        runHint={selectedRun?.hint}
        fetchDetail={fetchDetail}
        redactedMessage={redactedMessage}
        onClose={() => setSelectedRun(null)}
      />
    </>
  );
};

export default UsageRunsDialog;
